<template>
    <div class="relative w-full h-full flex items-center justify-center">
        <div
            :style="`height: ${props.isScreenAuto ? 'auto' : '180px'};`"
            v-if="!state.isLoaded"
        >
            <div
                class="
                    absolute
                    top-1
                    left-2
                    px-1
                    bg-[#00000075]
                    text-[12px] text-white
                "
            >
                {{ formatTime(state.duration) }}
            </div>
            <img
                :src="props.thumbnail"
                :style="`height: ${props.isScreenAuto ? 'auto' : '180px'};`"
                onerror="this.onerror=null;this.src='/default-images/team.png';this.style.backgroundImage = 'none';"
            />
        </div>
        <div
            class="
                absolute
                top-0
                left-0
                w-full
                h-full
                flex
                items-center
                justify-center
                text-white
                bg-black/30
                cursor-pointer
            "
            v-if="!state.isLoaded"
            v-on:click.stop="loadPlayer"
        >
            <ion-icon
                name="play"
                size="lg"
                style="font-size: 4em"
                v-if="!props.hidePlayButton"
            ></ion-icon>
        </div>
        <video
            class="w-full"
            ref="video"
            :src="props.url"
            @timeupdate="updateProgress"
            @loadedmetadata="updateDuration"
            @ended="onEnded"
            v-show="state.isLoaded"
        ></video>

        <div
            id="controls"
            class="
                flex flex-col
                px-2
                absolute
                w-full
                bottom-0
                text-white
                pt-10
                pb-2
                z-[100]
            "
            v-show="state.isLoaded"
        >
            <div class="flex gap-2 justify-between">
                <div class="flex gap-2 items-center">
                    <button
                        v-if="props.isPlaylist"
                        @click="emit('prev')"
                        :disabled="props.isPrevDisabled"
                    >
                        <ion-icon name="play-back"></ion-icon>
                    </button>

                    <button @click="togglePlay">
                        <ion-icon
                            name="pause"
                            v-if="state.isPlaying"
                        ></ion-icon>
                        <ion-icon name="play" v-else></ion-icon>
                    </button>

                    <button
                        v-if="props.isPlaylist"
                        @click="emit('next')"
                        :disabled="props.isNextDisabled"
                    >
                        <ion-icon name="play-forward"></ion-icon>
                    </button>
                </div>

                <div class="flex gap-2 items-center">
                    <!-- Fullscreen Button -->
                    <button @click="toggleFullscreen">
                        <ion-icon name="scan"></ion-icon>
                    </button>
                    <slot name="controls"></slot>
                </div>
            </div>

            <!-- Volume Control -->
            <input
                class="hidden"
                type="range"
                min="0"
                max="1"
                step="0.1"
                v-model="state.volume"
                @input="adjustVolume"
            />

            <input
                class="flex-grow"
                type="range"
                min="0"
                :max="state.duration"
                v-model="state.currentTime"
                @input="seekVideo"
            />
        </div>
    </div>
</template>

<script setup>
import { defineEmits, ref, watch, reactive, onMounted, onUnmounted } from "vue";
import { formatTime } from "../utils/timer";

const emit = defineEmits([
    "fullscreen",
    "prev",
    "next",
    "ended",
    "customplay",
    "play",
]);

const props = defineProps({
    data: {
        type: Object,
        required: false,
    },
    title: {
        type: String,
        required: false,
    },
    thumbnail: {
        type: String,
        required: false,
    },
    url: {
        type: String,
        required: true,
    },
    isScreenAuto: {
        type: Boolean,
        required: false,
        default: false,
    },
    customFullscreen: {
        type: Boolean,
        required: false,
        default: false,
    },
    isPlaylist: {
        type: Boolean,
        required: false,
        default: false,
    },
    isPrevDisabled: {
        type: Boolean,
        required: false,
        default: false,
    },
    isNextDisabled: {
        type: Boolean,
        required: false,
        default: false,
    },
    isCustomPlayClick: {
        type: Boolean,
        default: false,
    },
    hidePlayButton: {
        type: Boolean,
        default: false,
    },
    startSec: {
        type: Number,
        default: null,
    },
    endSec: {
        type: Number,
        default: null,
    },
});

// Refs
const video = ref(null);

const state = reactive({
    isLoaded: false,
    isPlaying: false,
    duration: 0,
    volume: 0,
    duration: 0,
    currentTime: 0,

    lastPlayTime: null,
});

// Methods
const togglePlay = () => {
    if (video.value.paused) {
        video.value.play();
        state.isPlaying = true;
        state.lastPlayTime = performance.now();
        startTimer();
    } else {
        video.value.pause();
        state.isPlaying = false;
        if (state.lastPlayTime) {
            const elapsed = (performance.now() - state.lastPlayTime) / 1000;
            trackWatchTime(elapsed);
            state.lastPlayTime = null;
        }
        stopTimer();
    }
};

function onEnded() {
    video.value.pause();
    state.isPlaying = false;
    if (state.lastPlayTime) {
        const elapsed = (performance.now() - state.lastPlayTime) / 1000;
        trackWatchTime(elapsed);
        state.lastPlayTime = null;
    }
    stopTimer();
}

let timer = null;
function startTimer() {
    if (!timer) {
        timer = setInterval(() => {
            const now = performance.now();
            if (state.lastPlayTime) {
                const elapsed = (now - state.lastPlayTime) / 1000;
                trackWatchTime(elapsed);
                state.lastPlayTime = now;
            }
        }, 5000);
    }
}

async function trackWatchTime(elapsed) {
    if (!props.data) {
        return;
    }
    await axios.post("/video/watch-time", {
        id: props.data?.id,
        watch_time: elapsed,
    });
}

function stopTimer() {
    if (timer) {
        clearInterval(timer);
        timer = null;
    }
}

const loadPlayer = () => {
    if (props.isCustomPlayClick) {
        emit("customplay");
        return;
    }
    emit("play");
    state.isLoaded = true;

    if (props.startSec !== null && video.value) {
        // Seek to clip start first; play only after the seek is confirmed
        const doPlay = () => {
            video.value.play();
            state.isPlaying = true;
            state.lastPlayTime = performance.now();
            startTimer();
        };
        video.value.addEventListener('seeked', doPlay, { once: true });
        video.value.currentTime = props.startSec;
    } else {
        togglePlay();
    }
};

const updateProgress = () => {
    const t = video.value.currentTime;
    if (props.endSec !== null && t >= props.endSec) {
        video.value.pause();
        video.value.currentTime = props.endSec;
        state.isPlaying = false;
        state.currentTime = clipDuration();
        stopTimer();
        emit('ended');
        return;
    }
    state.currentTime = props.startSec !== null ? t - props.startSec : t;
};

function clipDuration() {
    if (props.startSec !== null && props.endSec !== null) return props.endSec - props.startSec;
    if (props.endSec !== null) return props.endSec;
    return state.duration;
}

const updateDuration = () => {
    if (props.startSec !== null && props.endSec !== null) {
        state.duration = props.endSec - props.startSec;
        video.value.currentTime = props.startSec;
    } else if (props.startSec !== null) {
        state.duration = video.value.duration - props.startSec;
        video.value.currentTime = props.startSec;
    } else {
        state.duration = video.value.duration;
    }
};

const seekVideo = () => {
    const target = props.startSec !== null
        ? props.startSec + parseFloat(state.currentTime)
        : parseFloat(state.currentTime);
    video.value.currentTime = Math.min(target, props.endSec ?? Infinity);
};

const adjustVolume = () => {
    video.value.volume = state.volume;
};

const toggleFullscreen = () => {
    if (props.customFullscreen) {
        if (video.value.pause) {
            video.value.pause();
            state.isPlaying = false;
        }
        emit("fullscreen");
        return;
    }
    if (video.value?.requestFullscreen) {
        video.value.requestFullscreen();
    } else if (video.value?.webkitRequestFullscreen) {
        video.value.webkitRequestFullscreen();
    } else if (video.value?.mozRequestFullScreen) {
        video.value.mozRequestFullScreen();
    }
};

// Watch volume changes to apply them to the video element
watch(state.volume, (newValue) => {
    if (video.value) {
        video.value.volume = newValue;
    }
});

watch(
    () => props.url,
    (newValue, oldValue) => {
        if (!oldValue) {
            return;
        }

        if (!newValue) {
            state.isPlaying = false;
            return;
        }

        let interval = setInterval(() => {
            if (video.value.readyState > 0) {
                clearInterval(interval);
                video.value.play();
                state.isPlaying = true;
                state.duration = video.value.duration;
            }
            clearInterval(interval);
        }, 100);
    }
);

function play() {
    state.isPlaying = true;
}

onMounted(() => {
    let mountInterval = setInterval(() => {
        if (video.value.readyState > 0) {
            video.value.addEventListener("ended", () => {
                state.isPlaying = false;
                emit("ended");
            });
        }
        clearInterval(mountInterval);
    }, 100);

    document.addEventListener("visibilitychange", handleVisibilityChange);
});

onUnmounted(() => {
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    stopTimer();
});

function handleVisibilityChange() {
    if (document.hidden) {
        if (!video.value.paused) {
            video.value.pause();
            state.isPlaying = false;
            if (state.lastPlayTime) {
                const elapsed = (performance.now() - state.lastPlayTime) / 1000;
                trackWatchTime(elapsed);
                state.lastPlayTime = null;
            }
            stopTimer();
        }
    }
}
</script>

<style scoped>
#controls {
    padding-top: 10px;
    background: linear-gradient(0deg, black, #00000000);
}
</style>
